import React, { useState, useEffect, useCallback } from "react";
import { getSuppressions, addSuppression, removeSuppression, exportSuppressionsCsv } from "../../api";
import { MailX, Search, Trash2, RefreshCw, UserPlus, ShieldOff, Loader2, Download } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const sourceLabel = { "email-link": "Unsubscribe link", admin: "Added by staff", unknown: "Unknown" };

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 bg-white border border-[#f0ece5] rounded-xl px-4 py-3" data-testid={`suppress-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="w-9 h-9 rounded-lg bg-[#faf7f2] grid place-items-center text-[#b8935f]"><Icon size={18} /></div>
    <div>
      <div className="text-[20px] font-semibold text-[#2b2b2b] leading-none">{value}</div>
      <div className="text-[12px] text-[#8a8a8a] mt-1">{label}</div>
    </div>
  </div>
);

const AdminSuppressionList = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, shown: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const d = await getSuppressions(query);
      setItems(d.items || []);
      setStats(d.stats || { total: 0, shown: 0 });
    } catch {
      toast({ title: "Couldn't load suppression list", variant: "destructive" });
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const onSearch = (e) => { e.preventDefault(); load(q); };

  const onAdd = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast({ title: "Enter a valid email", variant: "destructive" }); return; }
    setAdding(true);
    try {
      await addSuppression(email);
      setNewEmail("");
      toast({ title: "Email suppressed", description: `${email} will no longer receive marketing.` });
      load(q);
    } catch (err) {
      toast({ title: "Couldn't add", description: err?.response?.data?.detail || "Please try again.", variant: "destructive" });
    }
    setAdding(false);
  };

  const onRemove = async (email) => {
    if (!window.confirm(`Re-subscribe ${email}? They will start receiving marketing emails again.`)) return;
    try {
      await removeSuppression(email);
      setItems((xs) => xs.filter((x) => x.email !== email));
      setStats((s) => ({ ...s, total: Math.max(0, (s.total || 1) - 1) }));
      toast({ title: "Removed from suppression list", description: `${email} can receive marketing again.` });
    } catch {
      toast({ title: "Remove failed", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-suppression-list">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-[22px] font-semibold text-[#2b2b2b] flex items-center gap-2"><ShieldOff size={20} className="text-[#b8935f]" /> Suppression List</h2>
          <p className="text-[13px] text-[#8a8a8a] mt-1">Everyone who has opted out of marketing email. These addresses are automatically skipped on all campaigns and bulk sends.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportSuppressionsCsv} disabled={!stats.total} data-testid="suppress-export-btn" className="flex items-center gap-2 text-[13px] px-3 py-2 rounded-lg border border-[#f0ece5] text-[#6a6a6a] hover:bg-[#faf7f2] disabled:opacity-50">
            <Download size={15} /> Export CSV
          </button>
          <button onClick={() => load(q)} data-testid="suppress-refresh-btn" className="flex items-center gap-2 text-[13px] px-3 py-2 rounded-lg border border-[#f0ece5] text-[#6a6a6a] hover:bg-[#faf7f2]">
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard icon={MailX} label="Total unsubscribed" value={stats.total || 0} />
        <StatCard icon={Search} label="Shown" value={stats.shown || 0} />
        <StatCard icon={MailX} label="Via unsubscribe link" value={(stats.bySource && stats.bySource["email-link"]) || 0} />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <form onSubmit={onSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8a8a8]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="suppress-search-input" placeholder="Search by email…" className="w-full h-[42px] pl-9 pr-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f]" />
          </div>
          <button type="submit" data-testid="suppress-search-btn" className="px-5 h-[42px] rounded-lg bg-[#1c1c1e] text-white text-[13px] font-medium hover:bg-black transition-colors">Search</button>
        </form>
        <form onSubmit={onAdd} className="flex gap-2">
          <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="suppress-add-input" placeholder="Manually suppress an email…" className="flex-1 h-[42px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f]" />
          <button type="submit" disabled={adding} data-testid="suppress-add-btn" className="inline-flex items-center gap-1.5 px-5 h-[42px] rounded-lg bg-[#b8935f] text-white text-[13px] font-medium hover:bg-[#a67f4d] transition-colors disabled:opacity-60">
            {adding ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Suppress
          </button>
        </form>
      </div>

      {loading ? (
        <div className="py-12 text-center text-[#8a8a8a] text-[14px]">Loading suppression list…</div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-[#8a8a8a] text-[14px] bg-white border border-[#f0ece5] rounded-xl">{q ? `No unsubscribes match “${q}”.` : "No one has unsubscribed yet."}</div>
      ) : (
        <div className="bg-white border border-[#f0ece5] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-[#faf7f2] text-[#8a8a8a] text-left text-[12px] uppercase tracking-wide">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-4 py-3 font-medium">Unsubscribed</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.email} className="border-t border-[#f5f1ea] hover:bg-[#fdfbf8]" data-testid={`suppress-row-${s.email}`}>
                    <td className="px-4 py-3 font-medium text-[#2b2b2b]">{s.email}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full text-[11px] bg-[#f0ece5] text-[#6a6a6a]">{sourceLabel[s.source] || sourceLabel.unknown}</span>
                    </td>
                    <td className="px-4 py-3 text-[#8a8a8a]">{fmtDate(s.at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => onRemove(s.email)} data-testid={`suppress-remove-${s.email}`} className="inline-flex items-center gap-1.5 text-[12px] text-[#c0392b] hover:bg-red-50 px-3 py-1.5 rounded-lg" title="Re-subscribe (remove)">
                        <Trash2 size={14} /> Re-subscribe
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSuppressionList;
